// Vendor Libs
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { filter, get, includes, isArray, map, orderBy, pick, startCase } from 'lodash';

// Components
import { Dropdown } from 'primereact/dropdown';
import BestMatchesItem from './best-matches-item';
import BulkMapping from './bulk-mapping';
import Button from '../core/button';
import Checkbox from '../core/checkbox';
import Error from '../core/error';
import FlagMetadata from './flag-metadata';
import Input from '../core/input';
import LanguageSelect from './language-select';
import Loading from '../core/loading';
import MappingDetail from './mapping-detail';
import ResetButton from '../core/reset-button';
import Select from '../core/select';
import ServicesRefreshPanelAction from './services-refresh-panel-action';
import ServicesSearchForm from './services-search-form';
import Thumbnail from '../core/thumbnail';

// Containers
import FlaggedMetadata from '../../containers/flagged-metadata';
import Mapping from '../../containers/mapping';
import withPermissions from '../../containers/with-permissions';
import withSearch from '../../containers/with-search';

// Styles
import './search-best-matches.scss';
import { DrawLegend, mappingColors } from '../core/colorizeRows';
import filterConfig from '../../../config/filter-config';

const servicesDataKey = 'bestMatches';

class SearchBestMatches extends Component {
  container = React.createRef();

  constructor(props) {
    super(props);
    this.state = {
      franchiseOnlyImages: get(props.servicesFormData, 'values.#franchiseOnlyImages') === 'true',
      filters: filterConfig.moviesAndProgramsMapping,
      selectedFilter: null,
    };
  }

  componentDidUpdate(nextProps) {
    const { fetching } = this.props;

    if (fetching && !nextProps.fetching) {
      if (this.container.current) {
        this.container.current.scrollIntoView();
      }
    }
  }

  onFilterChange(e) {
    const { code } = e.value;
    this.setState({ selectedFilter: code !== 'linkedNot' ? e.value : null });
  }

  getBestMatchesItemProps({ massShellActive, guid }, record, index) {
    return {
      ...pick(this.props, ['actionButton', 'buttonProps']),
      guid,
      key: `item-${get(record.result, 'echostarId') || index}-${get(record.result, 'roviProgramId')}`,
      massShellActive,
      record,
      renderThumbnails: this.renderThumbnails,
      isEpisodic: this.isEpisodic,
    };
  }

  getClass() {
    const { className } = this.props;

    return classnames(
      {
        'search-best-matches': true,
      },
      className
    );
  }

  handleShowEpisodicImagesClick = (event) => {
    this.setState({
      franchiseOnlyImages: event.target.checked,
    });
  };

  isEpisodic = (details) => get(details, 'echostarContentType') === 'SRE';

  renderExitSpecialModeButton = ({ bulkMapping, cancelMapping, massShellActive, showBulkMapping }) => (
        <FlaggedMetadata>
            {({ roviProgramId, flaggedMetadataReset }) => (massShellActive || (bulkMapping && !showBulkMapping) || roviProgramId ? (
                    <Button
                        className="movies-and-programs-mapping--exit-button"
                        onClick={roviProgramId ? flaggedMetadataReset : cancelMapping}
                    >
                        Exit {bulkMapping ? 'Bulk Mapping' : roviProgramId ? 'Flagging' : 'Mass Shell'}
                    </Button>
                ) : null)}
        </FlaggedMetadata>
  );

  renderFilters = () => {
    const { filters, selectedFilter } = this.state;
    return (
            <div className="search-best-matches--filters">
                <Dropdown
                    optionLabel="name"
                    options={filters}
                    placeholder="Select a Filter"
                    value={selectedFilter}
                    onChange={(e) => this.onFilterChange(e)}
                />
            </div>
    );
  };

  renderForm = () => (
        <ServicesSearchForm className="search-best-matches--form" noFocus={true} servicesDataKey={servicesDataKey}>
            <Select className="search-best-matches--form-field" label="Type" name="typeFilter" submitOnChange={true}>
                <option value="">Filter by Type</option>
                <option value="movies_only">Movies</option>
                <option value="episodes_only">Episodes</option>
                <option value="shows_only">Shows</option>
                <option value="sports_only">Special</option>
            </Select>
            <LanguageSelect
                className="search-best-matches--form-field"
                label="Title Languages"
                name="titleLanguages"
                submitOnBlur={true}
            />
            <Input
                className="search-best-matches--form-field"
                label="Release Year"
                name="releaseYear"
                size={10}
                submitOnBlur={true}
            />
            <Select
                className="search-best-matches--form-field"
                label="Adult Content"
                name="genres"
                submitOnChange={true}
            >
                <option value="">Show all content</option>
                <option value="!Adult">No adult content</option>
                <option value="Adult">Only adult content</option>
            </Select>
            <ResetButton className="search-best-matches--reset-button" />
            <div className="search-best-matches--checkboxes">
                <Checkbox
                    className="search-best-matches--image-checkbox"
                    label="Only Franchise Images"
                    name="#franchiseOnlyImages"
                    title="Toggle display of episodic and franchise background images."
                    value="true"
                    onClick={this.handleShowEpisodicImagesClick}
                />
                {this.renderFilters()}

                <DrawLegend data={mappingColors} />
            </div>
        </ServicesSearchForm>
  );

  renderThumbnails = (item) => {
    const amsItem = isArray(item) ? get(item, '0') : item;
    const { franchiseOnlyImages } = this.state;
    const images = orderBy(
      filter(
        filter(get(amsItem, 'images', []), 'url'),
        ({ category }) => !franchiseOnlyImages || includes(['Franchise', 'Movie16x9', 'Series16x9', 'Special16x9'], category),
      ),
      'category'
    );

    return images.length && !(this.isEpisodic(item) && franchiseOnlyImages) ? (
            <div className="search-best-matches--column-left">
                {map(images, ({ amsGalleryId, category, height, width, url }) => (
                    <Thumbnail
                        className="search-best-matches--thumbnail"
                        height={height}
                        key={amsGalleryId}
                        label={startCase(category)}
                        src={url}
                        width={width}
                    />
                ))}
            </div>
    ) : null;
  };

  filterList = () => {
    const { selectedFilter } = this.state;
    const { searchData } = this.props;
    let searchDataFiltered = searchData;
    if (selectedFilter) {
      const { code } = selectedFilter;
      if (code === 'linkedRoviAndTmsIds') {
        searchDataFiltered = searchData.filter(
          (s) => s.result?.tmsId?.length && s.result?.roviProgramId != null && s.result?.roviProgramId !== 0,
        );
      } else if (code === 'linkedRovi') {
        searchDataFiltered = searchData.filter(
          (s) => !s.result?.tmsId?.length
                        && !s.result?.sourceId?.length
                        && s.result?.roviProgramId != null
                        && s.result?.roviProgramId !== 0,
        );
      } else if (code === 'linkedTmsID') {
        searchDataFiltered = searchData.filter(
          (s) => s.result?.tmsId?.length
                        && !s.result?.sourceId?.length
                        && (s.result?.roviProgramId === null || s.result?.roviProgramId === 0),
        );
      } else if (code === 'linkedSourceID') {
        searchDataFiltered = searchData.filter(
          (s) => s.result?.sourceId?.length
                        && !s.result?.tmsId?.length
                        && (s.result?.roviProgramId === null || s.result?.roviProgramId === 0),
        );
      } else if (code === 'noRoviNoTmsNoSourceId') {
        searchDataFiltered = searchData.filter(
          (s) => !s.result?.tmsId?.length
                        && !s.result?.sourceId?.length
                        && (s.result?.roviProgramId === null || s.result?.roviProgramId === 0),
        );
      }
    }
    return searchDataFiltered;
  };

  renderList({ massShellActive, guid }, flaggedMetadata) {
    const { error, fetching } = this.props;
    const { echostarId, roviProgramId, tmsId, sourceId } = flaggedMetadata;
    const content = [];
    const searchDataFiltered = this.filterList();

    if (!error && !fetching && searchDataFiltered && searchDataFiltered.length) {
      map(searchDataFiltered, (record, index) => {
        if (record.result) {
          echostarId
                    && record.result.echostarId === echostarId
                    && ((roviProgramId && record.result.roviProgramId === roviProgramId)
                        || (tmsId && record.result.tmsId === tmsId)
                        || (sourceId && record.result.sourceId === sourceId))
            ? content.push(
                              <FlagMetadata
                                  headerRenderer={this.renderThumbnails}
                                  key={record.result.echostarId}
                                  searchData={[record.result]}
                              />,
            )
            : content.push(
                              <BestMatchesItem
                                  {...this.getBestMatchesItemProps({ massShellActive, guid }, record, index)}
                              />,
            );
        }
      });
    }

    return content;
  }

  renderBestMatches = ({ error, fetched, fetching, massShellActive, routerParams: { guid }, showMessage }) => {
    const { error: errorProp, fetching: fetchingProp } = this.props;

    return !error && !fetched && (!fetching || massShellActive) && guid && !showMessage ? (
            <>
                <ServicesRefreshPanelAction />
                {this.renderForm()}
                <Error error={errorProp} />
                <FlaggedMetadata>
                    {(flaggedMetadata) => this.renderList({massShellActive, guid}, flaggedMetadata)}
                </FlaggedMetadata>
                <Loading loading={fetchingProp} />
            </>
    ) : null;
  };

  renderContent = (mapping) => {
    const { showBulkMapping } = mapping;
    const { hasAccessByType } = this.props;

    return showBulkMapping ? (
            <BulkMapping {...mapping} />
    ) : (
            <>
                <MappingDetail {...mapping} hasAccessByType={hasAccessByType} />
                {this.renderBestMatches(mapping)}
            </>
    );
  };

  render() {
    return (
            <Mapping resetOnUnmount={true}>
                {(mapping) => {
                  const {
                    cancelMapping,
                    error,
                    fetching,
                    routerParams: { guid },
                  } = mapping;

                  return guid ? (
                        <div className={this.getClass()} ref={this.container}>
                            {this.renderExitSpecialModeButton(mapping)}
                            <Error error={error} onReset={cancelMapping} />
                            {this.renderContent(mapping)}
                            <Loading loading={fetching} />
                        </div>
                  ) : (
                        <Error error="Select a media item." />
                  );
                }}
            </Mapping>
    );
  }
}

SearchBestMatches.propTypes = {
  className: PropTypes.string,
  error: PropTypes.string,
  fetched: PropTypes.bool.isRequired,
  fetching: PropTypes.bool,
  hasAccessByType: PropTypes.func.isRequired,
  searchData: PropTypes.array.isRequired,
  servicesFormData: PropTypes.object.isRequired,
};

export default withPermissions(withSearch(SearchBestMatches, servicesDataKey));
